import { z } from 'zod';

export const semanticQueryModeSchema = z.enum(['single_asset', 'join', 'multi_query_merge']);
export type SemanticQueryMode = z.infer<typeof semanticQueryModeSchema>;

export const querySelectRoleSchema = z.enum(['metric', 'field']);
export type QuerySelectRole = z.infer<typeof querySelectRoleSchema>;

export const queryFilterOperatorSchema = z.enum([
  'equals',
  'not_equals',
  'in',
  'not_in',
  'contains',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'set',
  'not_set',
]);
export type QueryFilterOperator = z.infer<typeof queryFilterOperatorSchema>;

export const querySelectItemSchema = z.object({
  memberId: z.string().min(1),
  role: querySelectRoleSchema,
  alias: z.string().optional(),
});
export type QuerySelectItem = z.infer<typeof querySelectItemSchema>;

export const queryFilterSchema = z.object({
  memberId: z.string().min(1),
  operator: queryFilterOperatorSchema,
  value: z.unknown().optional(),
});
export type QueryFilter = z.infer<typeof queryFilterSchema>;

export const queryTimeGranularitySchema = z.enum(['day', 'week', 'month', 'quarter', 'year']);
export type QueryTimeGranularity = z.infer<typeof queryTimeGranularitySchema>;

export const queryTimeClauseSchema = z.object({
  memberId: z.string().min(1),
  granularity: queryTimeGranularitySchema.optional(),
  dateRange: z.union([z.string(), z.tuple([z.string(), z.string()])]).optional(),
});
export type QueryTimeClause = z.infer<typeof queryTimeClauseSchema>;

export const querySortDirectionSchema = z.enum(['asc', 'desc']);
export type QuerySortDirection = z.infer<typeof querySortDirectionSchema>;

export const querySortSchema = z.object({
  memberId: z.string().min(1),
  direction: querySortDirectionSchema,
});
export type QuerySort = z.infer<typeof querySortSchema>;

export const queryJoinTypeSchema = z.enum(['one_to_one', 'one_to_many', 'many_to_one', 'many_to_many']);
export type QueryJoinType = z.infer<typeof queryJoinTypeSchema>;

export const queryJoinEdgeSchema = z.object({
  sourceAssetId: z.string().min(1),
  targetAssetId: z.string().min(1),
  relationshipId: z.string().optional(),
  joinType: queryJoinTypeSchema.optional(),
});
export type QueryJoinEdge = z.infer<typeof queryJoinEdgeSchema>;

export const queryJoinStrategySchema = z.enum(['native_join', 'separate_then_merge']);
export type QueryJoinStrategy = z.infer<typeof queryJoinStrategySchema>;

export const queryJoinPlanSchema = z.object({
  path: z.array(queryJoinEdgeSchema),
  strategy: queryJoinStrategySchema,
});
export type QueryJoinPlan = z.infer<typeof queryJoinPlanSchema>;

export const queryContextSchema = z.object({
  timezone: z.string().optional(),
  locale: z.string().optional(),
  tenantId: z.string().optional(),
});
export type QueryContext = z.infer<typeof queryContextSchema>;

export const semanticQueryDraftSchema = z
  .object({
    connectionId: z.string().nullable(),
    mode: semanticQueryModeSchema,
    rootAssetId: z.string().nullable(),
    assetIds: z.array(z.string()),
    select: z.array(querySelectItemSchema),
    filters: z.array(queryFilterSchema),
    groupBy: z.array(z.string()),
    time: queryTimeClauseSchema.optional(),
    sort: z.array(querySortSchema),
    limit: z.number().int().positive().optional(),
    offset: z.number().int().min(0).optional(),
    parameters: z.record(z.string(), z.unknown()).default({}),
    joinPlan: queryJoinPlanSchema.optional(),
    context: queryContextSchema.optional(),
  })
  .passthrough();
export type SemanticQueryDraft = z.infer<typeof semanticQueryDraftSchema>;

export const dataConnectionQuerySchema = z.object({
  id: z.uuid(),
  dataConnectionId: z.uuid(),
  name: z.string(),
  description: z.string(),
  queryJson: semanticQueryDraftSchema,
  isDeleted: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
});
export type DataConnectionQuery = z.infer<typeof dataConnectionQuerySchema>;

export const listDataConnectionQueriesResponseSchema = z.object({
  items: z.array(dataConnectionQuerySchema),
});
export type ListDataConnectionQueriesResponse = z.infer<typeof listDataConnectionQueriesResponseSchema>;

export const createDataConnectionQueryBodySchema = z.object({
  name: z.string().trim().min(1),
  description: z.string().optional(),
  queryJson: semanticQueryDraftSchema,
});
export type CreateDataConnectionQueryBody = z.infer<typeof createDataConnectionQueryBodySchema>;

export const updateDataConnectionQueryBodySchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    description: z.string().optional(),
    queryJson: semanticQueryDraftSchema.optional(),
  })
  .refine((value) => value.name !== undefined || value.description !== undefined || value.queryJson !== undefined, {
    message: 'At least one field must be provided',
  });
export type UpdateDataConnectionQueryBody = z.infer<typeof updateDataConnectionQueryBodySchema>;

export const previewDataConnectionQueryBodySchema = z.object({
  queryJson: semanticQueryDraftSchema,
});
export type PreviewDataConnectionQueryBody = z.infer<typeof previewDataConnectionQueryBodySchema>;

export const previewDataConnectionQueryResponseSchema = z.object({
  columns: z.array(z.string()),
  rows: z.array(z.record(z.string(), z.unknown())),
  rowCount: z.number().int().nonnegative(),
  warnings: z.array(z.string()),
});
export type PreviewDataConnectionQueryResponse = z.infer<typeof previewDataConnectionQueryResponseSchema>;
